import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { ASSETS, PROJECTS, CATEGORIES } from "@/lib/projects";
import Lightbox from "@/components/Lightbox";

const Home = () => {
  const { t, lang } = useLang();
  const [activeProject, setActiveProject] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openProject = (project) => {
    setActiveProject(project);
    setLightboxIndex(0);
  };
  const closeLightbox = () => setActiveProject(null);

  // Lightbox items = images of the currently open project
  const lightboxItems = activeProject
    ? activeProject.images.map((src, idx) => ({
        id: `${activeProject.id}-${idx}`,
        src,
        alt: activeProject.title[lang],
        title: activeProject.title[lang],
        category:
          CATEGORIES.find((c) => c.id === activeProject.category)?.label[lang] ||
          "",
      }))
    : [];
  const activeItem = lightboxItems[lightboxIndex] || null;

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO */}
      <section
        className="relative h-screen w-full overflow-hidden"
        data-testid="hero-section"
      >
        <img
          src={ASSETS.hero}
          alt="Featured architectural render"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
          <div className="max-w-3xl text-white fade-up">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-[1px] bg-[#E11D2E]" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-medium opacity-90">
                {t.hero.eyebrow}
              </span>
            </div>
            <h1 className="font-display font-light leading-[0.9] tracking-tight text-5xl sm:text-7xl lg:text-[110px]">
              {t.hero.title1}
              <br />
              <span className="text-[#E11D2E]">{t.hero.title2}</span>
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-white/85 leading-relaxed">
              {t.hero.description}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="#projects"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("projects")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="fd-btn"
                data-testid="hero-cta-projects"
              >
                {t.hero.ctaProjects} <ArrowRight size={14} />
              </Link>
              <Link
                to="/contact"
                className="fd-btn fd-btn-ghost"
                data-testid="hero-cta-contact"
              >
                {t.hero.ctaContact}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section
        id="projects"
        className="py-24 md:py-32"
        data-testid="projects-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <div className="flex items-center gap-4 mb-5">
                <span className="w-10 h-[1px] bg-[#E11D2E]" />
                <span className="eyebrow">{t.projects.eyebrow}</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
                {t.projects.title}
              </h2>
            </div>
            <p className="max-w-md text-black/65 text-base leading-relaxed">
              {t.projects.description}
            </p>
          </div>

          {/* Bento masonry grid — 9 featured projects, one cover per project */}
          <div className="grid grid-cols-12 auto-rows-[180px] md:auto-rows-[220px] gap-4 md:gap-6">
            {/* Row 1 — Luxe Villa (big) + Donkere Stadswoning */}
            <ProjectCard
              project={PROJECTS[0]}
              className="col-span-12 md:col-span-8 row-span-2"
              testid="project-card-1"
              onOpen={openProject}
            />
            <ProjectCard
              project={PROJECTS[1]}
              className="col-span-12 md:col-span-4 row-span-2"
              testid="project-card-2"
              onOpen={openProject}
            />

            {/* Row 2 — Moderne Rijtjesvilla (big) + Houten Paviljoen */}
            <ProjectCard
              project={PROJECTS[2]}
              className="col-span-12 md:col-span-8 row-span-2"
              testid="project-card-3"
              onOpen={openProject}
            />
            <ProjectCard
              project={PROJECTS[4]}
              className="col-span-12 md:col-span-4 row-span-2"
              testid="project-card-4"
              onOpen={openProject}
            />

            {/* Row 3 — Groene A-Frame + Uitbouw + Tuinpaviljoen */}
            <ProjectCard
              project={PROJECTS[5]}
              className="col-span-12 md:col-span-4 row-span-2"
              testid="project-card-5"
              onOpen={openProject}
            />
            <ProjectCard
              project={PROJECTS[6]}
              className="col-span-12 md:col-span-4 row-span-2"
              testid="project-card-6"
              onOpen={openProject}
            />
            <ProjectCard
              project={PROJECTS[9]}
              className="col-span-12 md:col-span-4 row-span-2"
              testid="project-card-7"
              onOpen={openProject}
            />

            {/* Row 4 — Publieke Leefomgeving + Buurtspeelplaats */}
            <ProjectCard
              project={PROJECTS[7]}
              className="col-span-12 md:col-span-7 row-span-2"
              testid="project-card-8"
              onOpen={openProject}
            />
            <ProjectCard
              project={PROJECTS[8]}
              className="col-span-12 md:col-span-5 row-span-2"
              testid="project-card-9"
              onOpen={openProject}
            />
          </div>

          {/* View all CTA */}
          <div className="mt-12 md:mt-16 flex justify-center md:justify-end">
            <Link
              to="/portfolio"
              className="fd-btn fd-btn-outline-dark"
              data-testid="home-view-all-portfolio"
            >
              {t.nav.portfolio} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS / WERKWIJZE */}
      <section
        className="py-24 md:py-32 bg-white border-t border-black/10"
        data-testid="process-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-20">
            <div className="flex items-center gap-4 mb-5">
              <span className="w-10 h-[1px] bg-[#E11D2E]" />
              <span className="eyebrow">{t.process.eyebrow}</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
              {t.process.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {t.process.steps.map((step, i) => (
              <div
                key={step.n}
                className="group"
                data-testid={`process-step-${i + 1}`}
              >
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="font-display text-5xl font-light text-[#E11D2E]">
                    {step.n}
                  </span>
                  <span className="flex-1 h-[1px] bg-black/15" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-medium mb-4">
                  {step.title}
                </h3>
                <p className="text-black/65 leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section
        className="py-32 md:py-44 bg-black text-white relative overflow-hidden"
        data-testid="quote-section"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
          <span className="text-[#E11D2E] font-display text-7xl md:text-8xl leading-none block mb-4">
            “
          </span>
          <blockquote className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight">
            {t.quote.text}
          </blockquote>
          <p className="mt-10 text-[11px] uppercase tracking-[0.3em] text-white/60">
            {t.quote.author}
          </p>
        </div>
      </section>

      {/* LIGHTBOX */}
      <Lightbox
        open={!!activeProject}
        onClose={closeLightbox}
        item={activeItem}
        items={lightboxItems}
        onNavigate={(next) => {
          const idx = lightboxItems.findIndex((i) => i.id === next.id);
          if (idx >= 0) setLightboxIndex(idx);
        }}
      />
    </div>
  );
};

const ProjectCard = ({ project, className = "", testid, onOpen }) => {
  const { lang } = useLang();
  const catLabel =
    CATEGORIES.find((c) => c.id === project.category)?.label[lang] || "";
  return (
    <article
      className={`group relative overflow-hidden bg-black cursor-zoom-in ${className}`}
      data-testid={testid}
      onClick={() => onOpen && onOpen(project)}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && onOpen) {
          e.preventDefault();
          onOpen(project);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={project.title[lang]}
    >
      <img
        src={project.cover}
        alt={project.title[lang]}
        className="absolute inset-0 w-full h-full object-cover img-zoom"
      />
      <div className="absolute inset-0 project-overlay" />
      {project.images.length > 1 && (
        <div className="absolute top-4 right-4 px-2.5 py-1 bg-black/65 backdrop-blur-sm border border-white/20 text-white text-[10px] uppercase tracking-[0.2em] tabular-nums">
          +{project.images.length - 1}
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
        <div className="project-title">
          <p className="text-[10px] uppercase tracking-[0.28em] text-white/75 mb-2">
            {catLabel}
          </p>
          <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight">
            {project.title[lang]}
          </h3>
        </div>
      </div>
    </article>
  );
};

export default Home;
